import { useEffect, useState } from "react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff, Copy, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmtDate, EVENT_TYPES } from "@/lib/api";
import { BLOCKS, sectionTitle } from "@/components/blocks/registry";

export const CATEGORIES = [{ value: "generale", label: "Generale" }, ...EVENT_TYPES.map((t) => ({ value: t.value, label: `Servizio · ${t.label}` }))];
export const when = (d) => (d ? new Date(d).toLocaleString("it-IT", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const Row = ({ s, k, active, onSelect, onToggle, onDup, onDel }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1 border rounded-md bg-white px-1.5 py-1.5 ${active ? "border-slate-900" : "border-slate-200"} ${isDragging ? "shadow-lg z-10 relative" : ""} ${s.visible === false ? "opacity-50" : ""}`} data-testid={`section-row-${k}`}>
      <button type="button" {...attributes} {...listeners} className="p-1 text-slate-400 cursor-grab" aria-label="Trascina per riordinare" data-testid={`section-drag-${k}`}><GripVertical size={15} /></button>
      <button type="button" onClick={onSelect} className="flex-1 text-left min-w-0" data-testid={`section-select-${k}`}>
        <p className="text-[13px] font-medium truncate">{BLOCKS[s.type]?.label || s.type}</p><p className="text-[11px] text-slate-500 truncate">{sectionTitle(s) || "—"}</p>
      </button>
      <button type="button" onClick={onToggle} className="p-1.5 text-slate-500" title={s.visible === false ? "Mostra" : "Nascondi"} data-testid={`section-toggle-${k}`}>{s.visible === false ? <EyeOff size={14} /> : <Eye size={14} />}</button>
      <button type="button" onClick={onDup} className="p-1.5 text-slate-500" title="Duplica" data-testid={`section-dup-${k}`}><Copy size={14} /></button>
      <button type="button" onClick={onDel} className="p-1.5 text-terracotta" title="Elimina" data-testid={`section-del-${k}`}><Trash2 size={14} /></button>
    </div>
  );
};

export const SectionList = ({ sections, setSections, selected, setSelected }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const end = ({ active, over }) => { if (over && active.id !== over.id) setSections(arrayMove(sections, sections.findIndex((s) => s.id === active.id), sections.findIndex((s) => s.id === over.id))); };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={end}>
      <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-1.5" data-testid="section-list">
          {sections.map((s, k) => (
            <Row key={s.id} s={s} k={k} active={selected === s.id} onSelect={() => setSelected(s.id)}
              onToggle={() => setSections(sections.map((x) => (x.id === s.id ? { ...x, visible: x.visible === false } : x)))}
              onDup={() => { const c = { ...structuredClone(s), id: crypto.randomUUID() }; const a = [...sections]; a.splice(k + 1, 0, c); setSections(a); setSelected(c.id); }}
              onDel={() => { if (window.confirm(`Eliminare la sezione "${BLOCKS[s.type]?.label}"? Potrai recuperarla annullando le modifiche o dalla cronologia.`)) setSections(sections.filter((x) => x.id !== s.id)); }} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

export const diff = (pub, cur) => {
  if (!pub) return ["Prima pubblicazione della pagina."];
  const out = [];
  [["title", "Titolo della pagina"], ["seo_title", "Titolo SEO"], ["meta_description", "Meta description"], ["seo_image", "Immagine di condivisione"]].forEach(([k, l]) => pub[k] !== cur[k] && out.push(`${l} modificato`));
  const old = Object.fromEntries(pub.sections.map((s) => [s.id, s]));
  const ids = new Set(cur.sections.map((s) => s.id));
  cur.sections.forEach((s) => {
    const o = old[s.id];
    const name = `«${BLOCKS[s.type]?.label}${sectionTitle(s) ? `: ${sectionTitle(s)}` : ""}»`;
    if (!o) out.push(`Sezione aggiunta ${name}`);
    else {
      if (JSON.stringify(o.data) !== JSON.stringify(s.data)) out.push(`Sezione modificata ${name}`);
      if ((o.visible !== false) !== (s.visible !== false)) out.push(`Sezione ${s.visible === false ? "nascosta" : "resa visibile"} ${name}`);
    }
  });
  pub.sections.filter((s) => !ids.has(s.id)).forEach((s) => out.push(`Sezione eliminata «${BLOCKS[s.type]?.label}${sectionTitle(s) ? `: ${sectionTitle(s)}` : ""}»`));
  const order = (a) => a.filter((s) => ids.has(s.id) && old[s.id]).map((s) => s.id).join();
  if (order(pub.sections) !== order(cur.sections)) out.push("Ordine delle sezioni cambiato");
  return out.length ? out : ["Nessuna differenza rispetto alla versione pubblicata."];
};

export const PublishDialog = ({ open, onClose, changes, onConfirm }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="admin-root bg-white rounded-md max-w-lg" data-testid="publish-dialog">
      <DialogHeader><DialogTitle>Riepilogo delle modifiche</DialogTitle></DialogHeader>
      <ul className="text-sm space-y-1.5 max-h-[50vh] overflow-y-auto list-disc pl-5" data-testid="publish-changes">{changes.map((c, k) => <li key={k}>{c}</li>)}</ul>
      <p className="text-xs text-slate-500">La versione attuale del sito verrà sostituita. Potrai sempre ripristinarla dalla cronologia.</p>
      <div className="flex justify-end gap-2"><button onClick={onClose} className="btn-line !py-2 !px-4">Annulla</button><button onClick={onConfirm} className="btn-ink !py-2 !px-4" data-testid="publish-confirm-btn">Conferma e pubblica</button></div>
    </DialogContent>
  </Dialog>
);

export const HistoryDialog = ({ open, onClose, pageId, onRestored }) => {
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get(`/admin/pages/${pageId}/versions`).then((r) => setItems(r.data)); }, [open, pageId]);
  const restore = async (v) => {
    if (!window.confirm(`Caricare la versione del ${when(v.created_at)} come bozza? Non verrà pubblicata finché non confermi.`)) return;
    const { data } = await api.post(`/admin/pages/${pageId}/versions/${v.id}/restore`);
    onRestored(data); onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="admin-root bg-white rounded-md max-w-lg" data-testid="history-dialog">
        <DialogHeader><DialogTitle>Cronologia delle pubblicazioni</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Ultime 10 versioni pubblicate. Il ripristino crea una bozza, da rivedere e pubblicare.</p>
        <div className="divide-y divide-slate-100">{items.map((v, k) => (
          <div key={v.id} className="flex justify-between items-center py-2.5 text-sm" data-testid={`version-${k}`}>
            <span>{when(v.created_at)} {k === 0 && <span className="ml-2 text-[11px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800">online</span>}<span className="block text-xs text-slate-500">{v.content.sections.length} sezioni</span></span>
            <button onClick={() => restore(v)} className="text-sagedark text-sm" data-testid={`version-restore-${k}`}>Ripristina</button>
          </div>))}
          {!items.length && <p className="py-4 text-sm text-slate-500">Nessuna versione ancora.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const StatusBadges = ({ p, saving }) => (
  <div className="flex flex-wrap gap-2 text-[11px]" data-testid="page-status">
    {p.published ? <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800" data-testid="status-published">{p.status === "pubblicata" ? "Pubblicata" : "Ritirata (bozza)"} · ultima pubblicazione {when(p.published_at)}</span>
      : <span className="px-2 py-1 rounded bg-slate-100 text-slate-600">Mai pubblicata</span>}
    {p.draft && <span className="px-2 py-1 rounded bg-amber-50 text-amber-800" data-testid="status-draft">Bozza non pubblicata · {saving ? "salvataggio..." : `salvata ${when(p.draft_updated_at)}`}</span>}
    <span className="px-2 py-1 rounded bg-slate-50 text-slate-500">Ultima modifica {when(p.updated_at)}</span>
    {!p.visible && <span className="px-2 py-1 rounded bg-slate-100 text-slate-600">Nascosta</span>}
  </div>
);

export { fmtDate };
